
import { computed, defineComponent, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module compponents
import StepCompany from '@/modules/CreateOrderModule/components/StepCompany/index.vue'
import StepSenders from '@/modules/CreateOrderModule/components/StepSenders/index.vue'
import StepDate from '@/modules/CreateOrderModule/components/StepDate/index.vue'
import StepReceivers from '@/modules/CreateOrderModule/components/StepReceivers/index.vue'
import StepCargo from '@/modules/CreateOrderModule/components/StepCargo/index.vue'
import CompletedCargos from '@/modules/CreateOrderModule/components/CompletedCargos/index.vue'
import StepCreate from '@/modules/CreateOrderModule/components/StepCreate/index.vue'
import NewAdditionalServices from '@/modules/CreateOrderModule/components/NewAdditionalServices/index.vue'

// types
import { ReceiverT } from '@/core/types/receivers.types'
import { CargoT, CompletedCargoT } from '@/core/types/create_order.types'
import { CompanyT } from '@/core/types/companies.types'

// composable
import useCreateOrder from '@/modules/CreateOrderModule/composable/useCreateOrder'
import { useScroll } from '@/common/composable/useScroll'

// realisations
import { orders } from '@/core/realisations/orders.realisation'

import { SenderT } from '@/core/types/senders.types'
import { AdditionalServicesI } from '@/core/types/additional_services.types'

// constants
import { LTLHintText } from '@/core/constants/orders.constants'
import { EMPTY_ADDITIONAL_SERVICES } from '@/core/constants/additional_services.constants'
import { AdditionalByIdT } from '@/core/types/billing.types'
import { billing } from '@/core/realisations/billing.realisation'
const BOSCH_ID = 914

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        //
        'step-company': StepCompany,
        'step-senders': StepSenders,
        'step-date': StepDate,
        'step-receivers': StepReceivers,
        'step-cargo': StepCargo,
        'completed-cargos': CompletedCargos,
        'step-create': StepCreate,
        'new-additional-services': NewAdditionalServices,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        orderCreated: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const { scrollTo } = useScroll()

        const state = reactive({
            company_id: null as number | null,
            company_verify: null as number | null,
            is_billing_loading: false,
            additional_services: [] as AdditionalByIdT[],
            // sender
            sender_id: null as number | null,
            sender_city_id: null as number | null,
            sender_title: null as string | null,
            //
            sender_additional_services: { ...EMPTY_ADDITIONAL_SERVICES },
            is_sender_additional_services_setted: false,
            is_sender_additional_services_update: false,
            // date
            send_date: null as string | null,
            send_period: null as number | null,
            // receiver
            receiver: null as ReceiverT | null,
            // cargos
            completed_cargos: [] as CompletedCargoT[],
            // update
            is_updating_cargo: false,
            updating_cargo_index: null as number | null,
            updating_cargo: null as CompletedCargoT | null,
        })

        const loadBilling = async () => {
            try {
                state.is_billing_loading = true

                const response = await billing.getTariffThirdByCompanyId({
                    companyId: state.company_id as number,
                })

                state.additional_services = response.data
            } catch (error) {
                console.error(error)
            } finally {
                state.is_billing_loading = false
            }
        }

        // companies actions
        const onCompanySubmit = (company_id: number, company: CompanyT) => {
            state.company_id = company_id
            state.company_verify = company.verify

            if (company.billing_enabled) {
                loadBilling()
            }
        }

        const onCompanyReset = () => {
            state.company_id = null
            state.company_verify = null
            state.sender_id = null
            state.sender_additional_services = { ...EMPTY_ADDITIONAL_SERVICES }
            state.is_sender_additional_services_setted = false
            state.is_sender_additional_services_update = false
            state.sender_city_id = null
            state.completed_cargos = []
        }

        // senders actions
        const onSenderSubmit = (sender: SenderT) => {
            state.sender_id = sender.id
            state.sender_city_id = sender.city_id
            state.sender_title = sender.title
        }

        const onSenderReset = () => {
            state.sender_id = null
            state.sender_city_id = null
            state.sender_title = null
            state.sender_additional_services = { ...EMPTY_ADDITIONAL_SERVICES }
            state.is_sender_additional_services_setted = false
            state.is_sender_additional_services_update = false
            state.completed_cargos = []
        }

        // sender additional services actions
        const onSubmitSenderAdditionalServices = (additional_services: AdditionalServicesI) => {
            state.sender_additional_services = { ...additional_services }
            state.is_sender_additional_services_setted = true
        }

        const onStartUpdateSenderAdditionalServices = () => {
            state.is_sender_additional_services_setted = false
            state.is_sender_additional_services_update = true
        }

        // date actions
        const onDateSubmit = ({ date, period_id }: { date: string; period_id: number }) => {
            state.send_date = date
            state.send_period = period_id
        }

        const onDateReset = () => {
            state.send_date = null
            state.send_period = null
        }

        // receiver actions
        const onReceiverSubmit = (receiver: ReceiverT) => {
            state.receiver = receiver
        }

        const onReceiverReset = () => {
            state.receiver = null
            state.completed_cargos = []
        }

        // cargo actions
        const cancelUpdateCargo = () => {
            state.is_updating_cargo = false
            state.updating_cargo_index = null
            state.updating_cargo = null

            state.receiver = null
        }

        const onCargoSubmit = (cargo: CargoT) => {
            if (!state.receiver) return

            if (state.is_updating_cargo) {
                if (state.updating_cargo_index === null) return

                state.completed_cargos[state.updating_cargo_index].receiver = state.receiver
                state.completed_cargos[state.updating_cargo_index].logistics_info = cargo

                cancelUpdateCargo()
                return
            }

            const completed_cargo: CompletedCargoT = {
                receiver: state.receiver,
                logistics_info: cargo,
            }

            state.completed_cargos.unshift(completed_cargo)
            state.receiver = null
        }

        const onDeleteCargo = (completed_cargo_index: number) => {
            state.completed_cargos.splice(completed_cargo_index, 1)
        }

        const onStartUpdateCargo = (completed_cargo_index: number) => {
            const completed_cargo = state.completed_cargos[completed_cargo_index]
            if (!completed_cargo) return

            scrollTo('.StepCargo')

            state.updating_cargo_index = completed_cargo_index
            state.updating_cargo = completed_cargo
            state.is_updating_cargo = true

            state.receiver = completed_cargo.receiver
        }

        const onUpdateCompletedCargoAdditionalServices = ({
            index,
            additional_services,
        }: {
            index: number
            additional_services: AdditionalServicesI
        }) => {
            state.completed_cargos[index].logistics_info.additional_services.hasRisingToTheFloor =
                additional_services.hasRisingToTheFloor
            state.completed_cargos[index].logistics_info.additional_services.hasManipulator =
                additional_services.hasManipulator
            state.completed_cargos[index].logistics_info.additional_services.hasCrane =
                additional_services.hasCrane
            state.completed_cargos[index].logistics_info.additional_services.hasHydraulicTrolley =
                additional_services.hasHydraulicTrolley
            state.completed_cargos[index].logistics_info.additional_services.hasLoader =
                additional_services.hasLoader
        }

        const resetOrder = () => {
            state.updating_cargo = null
            state.updating_cargo_index = null
            state.is_updating_cargo = false

            state.completed_cargos = []

            state.receiver = null

            state.send_date = null
            state.send_period = null

            state.sender_id = null
            state.sender_city_id = null
            state.sender_title = null
            state.sender_additional_services = { ...EMPTY_ADDITIONAL_SERVICES }
            state.is_sender_additional_services_setted = false
            state.is_sender_additional_services_update = false

            state.company_id = null
            state.company_verify = null

            emit('reset')
        }

        const { orderPayload } = useCreateOrder()

        const onSubmitOrder = async () => {
            const payload = orderPayload(state)
            if (!payload) return

            emit('submit', payload)
        }

        // computeds
        const isCompanySelected = computed(() => Boolean(state.company_id))
        const isBosch = computed(() => state.company_id === BOSCH_ID)
        const isSenderSelected = computed(
            () => Boolean(state.sender_id) && Boolean(state.sender_city_id)
        )
        const isDateSelected = computed(() => state.send_date && state.send_period)
        const isReceiverSelected = computed(() => Boolean(state.receiver))

        const selectedReceiversIds = computed(() => {
            return state.completed_cargos.map((completed_cargo) => completed_cargo.receiver.id)
        })

        return {
            state,
            // company
            onCompanySubmit,
            onCompanyReset,
            // sender
            onSenderSubmit,
            onSenderReset,
            //
            onSubmitSenderAdditionalServices,
            onStartUpdateSenderAdditionalServices,
            // date
            onDateSubmit,
            onDateReset,
            // receiver
            onReceiverSubmit,
            onReceiverReset,
            // cargo
            onCargoSubmit,
            cancelUpdateCargo,
            onDeleteCargo,
            onStartUpdateCargo,
            // computeds
            isCompanySelected,
            isBosch,
            isSenderSelected,
            isDateSelected,
            isReceiverSelected,
            selectedReceiversIds,
            // create
            onSubmitOrder,
            resetOrder,
            onUpdateCompletedCargoAdditionalServices,
            //
            LTLHintText,
        }
    },
})
